import { generateCourseMaterialUrl } from '@/helpers/generatePathname';
import { convertHHMMSSToMMSS } from '@/helpers/timeHelpers';
import useToast from '@/hooks/useToast';
import {
  ICourseDetailResponse,
  TCourseMaterial,
} from '@/interfaces/training.interface';
import {
  CourseDetailRow,
  CourseDetailRowStatus,
} from 'design-system/components/CourseDetailRow';
import { useRouter } from 'next/router';
import { UrlObject } from 'url';
import RequiredJoinGroupHOC from '../lesson/RequiredJoinGroupHOC';
import LockLessonOverlay from './LockLessonOverlay';

type CourseMaterialsListProps = {
  courseMaterials: TCourseMaterial[];
  eachLock: boolean;
  fullyLock: boolean;
  requiresVerified: boolean;
  isPrivateGroup: boolean;
  fullAccess: boolean;
} & Pick<ICourseDetailResponse, 'allowLessonSkipping' | 'isOwner'>;

const CourseMaterialsList = (props: CourseMaterialsListProps) => {
  const {
    courseMaterials,
    requiresVerified,
    eachLock,
    fullyLock,
    isPrivateGroup,
    allowLessonSkipping,
    isOwner,
  } = props;
  const router = useRouter();
  const { cid } = router.query;

  if (fullyLock && isPrivateGroup) return <LockLessonOverlay />;

  return (
    <div className="flex flex-col gap-3">
      {courseMaterials?.map((courseMaterial, index) => {
        const courseDetailRowStatus =
          fullyLock || eachLock
            ? 'lock'
            : courseMaterial.isCompleted
            ? 'finish'
            : 'ready';
        return (
          <Wrapper
            key={courseMaterial.id}
            courseStatus={courseDetailRowStatus}
            requiresVerified={requiresVerified}
            href={generateCourseMaterialUrl({
              materialId: courseMaterial.id,
              materialType: courseMaterial.materialType,
              courseId: cid as string,
            })}
            allowLessonSkipping={allowLessonSkipping}
            isPreviousLessonCompleted={
              index - 1 < 0 ? true : courseMaterials[index - 1].isCompleted
            }
            isOwner={isOwner}
          >
            <CourseDetailRow
              id={courseMaterial.id}
              status={courseMaterial.isCompleted ? 'finish' : 'ready'}
              title={(courseMaterial as any).title}
              duration={convertHHMMSSToMMSS(courseMaterial.duration)}
              materialType={courseMaterial.materialType}
              allowTrial={
                courseDetailRowStatus === 'lock' && courseMaterial.isTrial
              }
            />
          </Wrapper>
        );
      })}
    </div>
  );
};

const Wrapper = ({
  children,
  courseStatus,
  requiresVerified,
  href,
  allowLessonSkipping,
  isPreviousLessonCompleted,
  isOwner,
}: {
  children: any;
  courseStatus: CourseDetailRowStatus;
  requiresVerified: boolean;
  href: string | UrlObject;
  isPreviousLessonCompleted: boolean;
} & Pick<ICourseDetailResponse, 'allowLessonSkipping' | 'isOwner'>) => {
  const toast = useToast();

  return courseStatus === 'ready' || courseStatus === 'finish' ? (
    <RequiredJoinGroupHOC
      onAfterJoiningGroup={() => {
        if (!allowLessonSkipping && !isPreviousLessonCompleted && !isOwner) {
          toast(
            'Bạn cần hoàn thành bài học trước đó để xem bài học này.',
            'error',
            {
              duration: 5 * 1000,
            }
          );
        }
      }}
      href={
        allowLessonSkipping ||
        (!allowLessonSkipping && isPreviousLessonCompleted) ||
        isOwner
          ? href
          : null
      }
      isRequiresVerified={requiresVerified}
    >
      {children}
    </RequiredJoinGroupHOC>
  ) : (
    children
  );
};

export default CourseMaterialsList;
